import axios, { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { getApiBaseUrlDynamic, API_TIMEOUT, checkApiAvailability } from '../config/api.config';
import { logger } from '../utils/logger';
import { API_ENDPOINTS } from '../constants/api.constants';

// Функция для получения CSRF токена из cookie
const getCsrfToken = (): string | null => {
    const name = 'XSRF-TOKEN';
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        return parts.pop()?.split(';').shift() || null;
    }
    return null;
};

class ApiService {
    public api: AxiosInstance;
    private csrfTokenPromise: Promise<string | null> | null = null;
    private isRefreshing: boolean = false;
    private failedQueue: Array<{
        resolve: (value?: any) => void;
        reject: (reason?: any) => void;
    }> = [];

    constructor() {
        this.api = axios.create({
            // Не устанавливаем baseURL здесь - будем устанавливать в interceptor
            timeout: API_TIMEOUT,
            headers: {
                'Content-Type': 'application/json',
            },
            withCredentials: true,
            xsrfCookieName: 'XSRF-TOKEN',
            xsrfHeaderName: 'X-CSRF-Token'
        });

        // Request interceptor - устанавливаем baseURL динамически при каждом запросе
        this.api.interceptors.request.use(
            async (config: InternalAxiosRequestConfig) => {
                // ВСЕГДА устанавливаем baseURL динамически при каждом запросе
                const dynamicBaseUrl = getApiBaseUrlDynamic();
                config.baseURL = dynamicBaseUrl;                
                // Проверяем доступность API перед каждым запросом
                // Но пропускаем проверку для критичных запросов (health check, auth, users/me)
                const isCriticalRequest = config.url?.includes('/health') || 
                                         config.url?.includes('/auth') || 
                                         config.url?.includes('/users/me');
                
                if (!isCriticalRequest) {
                    try {
                        const isAvailable = await checkApiAvailability();
                        if (!isAvailable) {
                            throw new Error('API is not available');
                        }
                    } catch (error) {
                        // Игнорируем ошибки проверки доступности для критичных запросов
                        
                    }
                }

                // Добавляем CSRF токен для POST, PUT, DELETE, PATCH запросов
                // Axios автоматически добавляет токен из cookie XSRF-TOKEN в заголовок X-CSRF-Token
                // благодаря настройкам xsrfCookieName и xsrfHeaderName
                // Но для надежности также добавляем вручную, если токен есть
                const needsCsrf = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(config.method?.toUpperCase() || '');
                const isExemptPath = config.url?.includes('/auth/login') || 
                                    config.url?.includes('/auth/register') ||
                                    config.url?.includes('/csrf-token');
                
                if (needsCsrf && !isExemptPath) {
                    // Получаем CSRF токен из cookie
                    let csrfToken = getCsrfToken();
                    
                    // Если токена нет, пытаемся получить его с сервера
                    if (!csrfToken && !this.csrfTokenPromise) {
                        this.csrfTokenPromise = this.fetchCsrfToken();
                        csrfToken = await this.csrfTokenPromise;
                        this.csrfTokenPromise = null;
                    } else if (this.csrfTokenPromise) {
                        csrfToken = await this.csrfTokenPromise;
                        this.csrfTokenPromise = null;
                    }
                    
                    // Явно добавляем токен в заголовок (axios тоже добавит автоматически, но это для надежности)
                    if (csrfToken && config.headers) {
                        config.headers['X-CSRF-Token'] = csrfToken;
                    }
                }

                // Токены теперь в httpOnly cookies, они отправляются автоматически
                // Не нужно устанавливать Authorization заголовок
               
                return config;
            },
            (error) => {
                logger.error('API Request Error:', error);
                return Promise.reject(error);
            }
        );

        // Response interceptor
        this.api.interceptors.response.use(
            (response) => {                
                return response;
            },
            async (error) => {
                if (error.code === 'ERR_NETWORK') {
                    logger.error('Network Error - API is not available');
                    return Promise.reject(new Error('Сервер недоступен. Пожалуйста, попробуйте позже.'));
                }

                const status = error?.response?.status;
                const originalRequest = error.config;

                // Если ошибка 403 из-за отсутствия CSRF токена, получаем новый токен и повторяем запрос
                if (status === 403 && 
                    error?.response?.data?.message === 'CSRF token missing' &&
                    originalRequest && 
                    !originalRequest._retry) {
                    
                    originalRequest._retry = true;
                    
                    try {
                        // Получаем новый CSRF токен
                        await this.fetchCsrfToken();
                        // Повторяем запрос
                        return this.api(originalRequest);
                    } catch (csrfError) {
                        logger.error('Failed to refresh CSRF token:', csrfError);
                        return Promise.reject(error);
                    }
                }

                // Обработка 401 ошибок - автоматическое обновление токена
                if (status === 401 && originalRequest && !originalRequest._retry) {
                // Пропускаем refresh для auth endpoints, чтобы избежать бесконечного цикла
                    const isAuthEndpoint = originalRequest.url?.includes('/auth/login') ||
                                         originalRequest.url?.includes('/auth/register') ||
                                         originalRequest.url?.includes('/auth/refresh');

                    // Пропускаем refresh для /users/me, так как это запрос для проверки авторизации
                    // и для неавторизованных пользователей это нормальная ситуация
                    const isAuthCheckEndpoint = originalRequest.url?.includes('/users/me') ||
                                                originalRequest.url?.includes(API_ENDPOINTS.USERS.ME);
                    
                    if (!isAuthEndpoint && !isAuthCheckEndpoint) {
                        originalRequest._retry = true;
                        
                        try {
                            // Если уже идет процесс обновления токена, добавляем запрос в очередь
                            if (this.isRefreshing) {
                                return new Promise((resolve, reject) => {
                                    this.failedQueue.push({ resolve, reject });
                                })
                                    .then(() => {
                                        return this.api(originalRequest);
                                    })
                                    .catch((err) => {
                                        return Promise.reject(err);
                                    });
                            }
                            
                            // Начинаем обновление токена
                            this.isRefreshing = true;
                            await this.refreshAuthToken();
                            this.isRefreshing = false;
                            
                            // Выполняем все запросы из очереди
                            this.processQueue(null);
                            
                            // Повторяем исходный запрос с новым токеном
                            return this.api(originalRequest);
                        } catch (refreshError) {
                            this.isRefreshing = false;
                            // Обрабатываем все запросы из очереди с ошибкой
                            this.processQueue(refreshError);
                            
                            // Если refresh токен истек, редиректим на login только для защищенных маршрутов
                            // Не редиректим, если пользователь просматривает публичные страницы (например, турниры)
                            const isPublicRoute = window.location.pathname.startsWith('/tournaments/') ||
                                                window.location.pathname === '/' ||
                                                window.location.pathname === '/tournaments';
                            
                            if (!isPublicRoute && window.location.pathname !== '/login') {
                                logger.warn('Token refresh failed, redirecting to login:', refreshError);
                                (refreshError as any).sessionExpired = true;
                                window.location.href = '/login';
                            } else {
                                (refreshError as any).sessionExpired = true;
                            }
                            return Promise.reject(refreshError);
                        }
                    } else {
                        // Для auth endpoints и auth check просто логируем как debug
                        logger.debug('Unauthorized request to auth endpoint (expected for unauthenticated users)');
                    }
                } else if (status === 401) {
                    // Если это повторная попытка после refresh или auth endpoint — помечаем для UI
                    (error as any).sessionExpired = true;
                    logger.debug('Unauthorized request (expected for unauthenticated users)');
                } 
                // Логируем 429 ошибки как WARN, так как это ожидаемое поведение при rate limiting
                else if (status === 429) {
                    const retryAfter = error?.response?.data?.retryAfter;
                    const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Слишком много запросов';
                    logger.warn('Rate limit exceeded:', {
                        url: originalRequest?.url,
                        retryAfter: retryAfter ? new Date(retryAfter * 1000).toISOString() : 'unknown',
                        message: errorMessage
                    });
                } 
                // Логируем 500 ошибки для auth endpoints как debug (для неавторизованных пользователей это нормально)
                else if (status === 500 && originalRequest?.url?.includes('/auth/refresh')) {
                    
                }
                // Логируем остальные ошибки как ERROR
                // НЕ логируем 401 ошибки для /users/me, так как это ожидаемое поведение при выходе из системы
                else if (status !== 403 || error?.response?.data?.message !== 'CSRF token missing') {
                    const isAuthCheckEndpoint = originalRequest?.url?.includes('/users/me') ||
                                                 originalRequest?.url?.includes(API_ENDPOINTS.USERS.ME);
                    if (status === 401 && isAuthCheckEndpoint) {
                        // Для /users/me при 401 логируем как debug, так как это ожидаемое поведение
                        // И не показываем ошибку в консоли браузера
                        logger.debug('Unauthorized request to /users/me (expected during logout or for unauthenticated users)');
                        // Возвращаем ошибку без логирования в консоль браузера
                        return Promise.reject(error);
                    } else {
                        logger.error('API Response Error:', {
                            status,
                            data: error?.response?.data,
                            message: error?.message,
                            code: error?.code
                        });
                    }
                }

                return Promise.reject(error);
            }
        );
    }

    // Метод для получения CSRF токена с сервера
    private async fetchCsrfToken(): Promise<string | null> {
        try {
            const response = await this.api.get<{ status: string; csrfToken: string }>('/csrf-token');
            return response.data.csrfToken || null;
        } catch (error) {
            logger.debug('Failed to fetch CSRF token:', error);
            return null;
        }
    }

    // Метод для обновления auth токена
    private async refreshAuthToken(): Promise<void> {
        try {
            const response = await this.api.post('/auth/refresh');
            
            if (response.status === 200) {
                logger.debug('Auth token refreshed successfully');
                // Токены обновлены в httpOnly cookies сервером автоматически
            } else {
                throw new Error('Failed to refresh token');
            }
        } catch (error: any) {
            const status = error?.response?.status;
            // 401 (истёк/невалидный refresh) или 500 (ошибка сервера/конфигурации) — не логируем как ERROR
            if (status === 401 || status === 500) {
                logger.warn('Refresh token failed (session invalid or server error). Sign in again if needed.');
            } else {
                logger.error('Failed to refresh auth token:', error);
            }
            throw error;
        }
    }

    // Метод для обработки очереди запросов после обновления токена
    private processQueue(error: any) {
        this.failedQueue.forEach((promise) => {
            if (error) {
                promise.reject(error);
            } else {
                promise.resolve();
            }
        });
        this.failedQueue = [];
    }

    // Добавляем метод для установки заголовка авторизации
    setAuthToken(token: string | null) {
        if (token) {
            this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else {
            delete this.api.defaults.headers.common['Authorization'];
        }
    }

    async get<T>(url: string, config?: AxiosRequestConfig) {
        return this.api.get<T>(url, config);
    }

    async post<T>(url: string, data?: any, config?: AxiosRequestConfig) {
        return this.api.post<T>(url, data, config);
    }

    async put<T>(url: string, data?: any, config?: AxiosRequestConfig) {
        return this.api.put<T>(url, data, config);
    }

    async delete<T>(url: string, config?: AxiosRequestConfig) {
        return this.api.delete<T>(url, config);
    }

    async patch<T>(url: string, data?: any, config?: AxiosRequestConfig) {
        return this.api.patch<T>(url, data, config);
    }
}

export const apiService = new ApiService();
export default apiService;
